import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { DsToastModel, DsToastService } from '@bmw-ds/components';
import { TranslateService } from '@ngx-translate/core';
import { finalize, map, Subject } from 'rxjs';
import { FileType } from 'src/app/shared/enums/file-type.enum';
import { MockedEndpoint } from 'src/app/shared/models/backoffice/mocked-endpoint.model';
import { MockedFile } from 'src/app/shared/models/backoffice/mocked-file.model';
import { QueueFile } from 'src/app/shared/models/backoffice/queue-cases/queue-file.model';
import { DeleteFileComponent } from 'src/app/views/modals/delete-file-modal/delete-file.component';
import { PreviewFileComponent } from 'src/app/views/modals/preview-file-modal/preview-file-modal.component';
import { UploadQueueFileComponent } from 'src/app/views/modals/upload-queue-file/upload-queue-file.component';
import { ComfyActionPlanModalComponent } from 'src/app/views/modals/comfy-action-plan-modal/comfy-action-plan-modal.component';

import { BaseTone } from '../../shared/enums/tone.enum';
import { MockedVin } from '../../shared/models/backoffice/mocked-vin.model';
import { BackofficeService } from '../services/backoffice.service';
import { LocaleService } from '../services/locale.service';
import { ModalService } from '../services/modal.service';
import { ActionPlan } from 'src/app/shared/models/backoffice/action-plan.model';
import { DEFAULT_ANGULAR_LOCALE } from 'src/app/shared/utils/language.utils';

@Injectable()
export class BackofficeStore {
  private readonly backofficeService = inject(BackofficeService);
  private readonly translateService = inject(TranslateService);
  private readonly toastService = inject(DsToastService);
  private readonly modalService = inject(ModalService);
  private readonly routerService = inject(Router);
  private readonly localeService = inject(LocaleService);

  private datePipe = new DatePipe(DEFAULT_ANGULAR_LOCALE);

  private vinDeleted = new Subject<void>();

  readonly vinDeleted$ = this.vinDeleted.asObservable();
  readonly mockedVinData = signal<MockedVin[] | undefined>(undefined);
  readonly actionPlan = signal<ActionPlan[] | undefined>(undefined);

  readonly hasLoadError = signal<boolean>(false);
  readonly mockedVin = signal<MockedVin>({} as MockedVin);

  readonly isExecutingAction = signal<boolean>(false);

  loadMockedVins(destroy: DestroyRef): void {
    this.backofficeService
      .getAllMockedVinData()
      .pipe(
        takeUntilDestroyed(destroy),
        map((mockedVin) => this.mapMockedVins(mockedVin))
      )
      .subscribe({
        next: (value) => {
          this.setMockedVinData(value);
          this.hasLoadError.set(false);
        },
        error: (err) => {
          this.hasLoadError.set(true);
          console.error(err);
        },
      });
  }

  addVin(vin: string): void {
    this.isExecutingAction.set(true);
    this.backofficeService
      .addMockedVin(vin)
      .pipe(finalize(() => this.isExecutingAction.set(false)))
      .subscribe({
        next: () => {
          this.goToMockedInterface(vin);
          this.hasLoadError.set(false);
        },
        error: (err) => {
          this.hasLoadError.set(true);
          console.error(err);
        },
      });
  }

  deleteVin(vin: string): void {
    this.isExecutingAction.set(true);
    this.backofficeService
      .deleteMockedVin(vin)
      .pipe(finalize(() => this.isExecutingAction.set(false)))
      .subscribe({
        next: () => {
          this.vinDeleted.next();
          const successMessage = this.translateService.instant('backoffice.deleteVin.success', { vin });
          this.sendToast('vin-deleting-success-toast', BaseTone.POSITIVE, successMessage);
          this.hasLoadError.set(false);
        },
        error: (err: HttpErrorResponse) => {
          this.vinDeleted.next();
          let errorMessage: string;
          this.hasLoadError.set(true);

          if (err.status === 404) {
            errorMessage = this.translateService.instant('backoffice.deleteVin.error.notFound');
            this.sendToast('vin-deleting-error-toast', BaseTone.CAUTION, errorMessage);
          } else {
            errorMessage = this.translateService.instant('backoffice.deleteVin.error.generic');
            this.sendToast('vin-deleting-error-toast', BaseTone.CRITICAL, errorMessage);
          }
        },
      });
  }

  loadMockedVinData(vin: string): void {
    this.backofficeService
      .getMockedVinData(vin)
      .pipe(
        map((mockedVinInfo) => ({
          ...mockedVinInfo,
          mockedInterface: mockedVinInfo.mockedInterface
            ? mockedVinInfo.mockedInterface.sort((a, b) => a.pureInterface.localeCompare(b.pureInterface))
            : [],
        }))
      )
      .subscribe({
        next: (sortedMockedVinInfo) => {
          this.hasLoadError.set(false);
          this.mockedVin.set(sortedMockedVinInfo);
          this.hasLoadError.set(false);
        },
        error: (err: HttpErrorResponse) => {
          this.hasLoadError.set(true);
          console.error(err);
          const errorMessage = this.translateService.instant(err.status === 404 ? 'error.invalidVin' : 'error.generic');
          this.sendToast('mocked-vin-data-fetch-failed', BaseTone.CRITICAL, errorMessage);
          this.navigateToMocking(vin);
        },
      });
  }

  resetVinInformationFromDatabase(vin: string): void {
    this.isExecutingAction.set(true);
    this.backofficeService
      .resetCaseByVin(vin)
      .pipe(finalize(() => this.isExecutingAction.set(false)))
      .subscribe({
        next: () => {
          const successMessage = this.translateService.instant('common.success');
          this.sendToast('vin-information-reset-success', BaseTone.POSITIVE, successMessage);
          this.hasLoadError.set(false);
        },
        error: () => {
          this.hasLoadError.set(true);
          const errorMessage = this.translateService.instant('error.generic');
          this.sendToast('vin-information-reset-failed', BaseTone.CRITICAL, errorMessage);
        },
      });
  }

  selectedFileChange(vin: string, endpoint: MockedEndpoint, fileId: number): void {
    this.backofficeService.updateMockedEndpointSelectedFile(vin, endpoint.id, fileId).subscribe({
      next: () => {
        const successMessage = this.translateService.instant('common.success');
        this.sendToast('selected-file-update-success', BaseTone.POSITIVE, successMessage);

        endpoint.files.forEach((file) => {
          file.isActive = file.id === fileId;
        });
        endpoint.activeFile = fileId;

        const newMockedVin = structuredClone(this.mockedVin());
        this.mockedVin.set(newMockedVin);
        this.hasLoadError.set(false);
      },
      error: () => {
        this.hasLoadError.set(true);
        const errorMessage = this.translateService.instant('error.generic');
        this.sendToast('selected-file-update-failed', BaseTone.CRITICAL, errorMessage);
      },
    });
  }

  mockStatusChanged(vin: string, status: boolean, mockedEndpoint: MockedEndpoint): void {
    this.backofficeService.updateMockedEndpoint(vin, mockedEndpoint.id, status).subscribe({
      next: () => {
        const successMessage = this.translateService.instant('common.success');
        this.sendToast('mocked-endpoint-update-success', BaseTone.POSITIVE, successMessage);

        mockedEndpoint.isMocked = status;

        const newMockedVin = structuredClone(this.mockedVin());
        this.mockedVin.set(newMockedVin);
        this.hasLoadError.set(false);
      },
      error: () => {
        this.hasLoadError.set(true);
        const errorMessage = this.translateService.instant('error.generic');
        this.sendToast('mocked-endpoint-update-failed', BaseTone.CRITICAL, errorMessage);
        // TODO: Discover how to revert the change
      },
    });
  }

  fileAdded(vin: string, event: Event, mockedEndpoint: MockedEndpoint): void {
    const inputFiles = (event.target as HTMLInputElement).files;

    if (inputFiles && inputFiles?.length > 0) {
      this.backofficeService.addFileToMockedEndpoint(vin, mockedEndpoint.id, inputFiles[0]).subscribe({
        next: (createdFile) => {
          const successMessage = this.translateService.instant('common.success');
          this.sendToast('add-file-success', BaseTone.POSITIVE, successMessage);

          mockedEndpoint.files.push(createdFile);

          const newMockedVin = structuredClone(this.mockedVin());
          this.mockedVin.set(newMockedVin);
          this.hasLoadError.set(false);
        },
        error: (err: HttpErrorResponse) => {
          const errorMessage = this.translateService.instant(
            err.status === 400 ? 'error.invalidFile' : 'error.generic'
          );
          this.sendToast('add-file-failed', BaseTone.CRITICAL, errorMessage);
        },
      });
    }
  }

  deleteFile(vin: string, file: MockedFile, type: FileType, mockedEndpoint?: MockedEndpoint): void {
    const inputs = new Map<string, unknown>([
      ['vin', vin],
      ['file', file],
      ['type', type],
    ]);
    const deleteFileComponent = this.modalService.openDialog(DeleteFileComponent, inputs);

    deleteFileComponent.fileDeleted.subscribe((fileWasDeleted) => {
      if (!fileWasDeleted) {
        return;
      }

      if (type === FileType.ENDPOINT) {
        if (mockedEndpoint?.activeFile === file.id) {
          mockedEndpoint.activeFile = 0;
        }

        /// isn't this backwards?
        mockedEndpoint!.files = mockedEndpoint!.files.filter((currentFile) => currentFile.id !== file.id);
        const newMockedVin = structuredClone(this.mockedVin());
        this.mockedVin.set(newMockedVin);
      } else {
        const newMockedVin = structuredClone(this.mockedVin());
        newMockedVin.mockedQueue = newMockedVin.mockedQueue.filter((currentFile) => currentFile.id !== file.id);
        this.mockedVin.set(newMockedVin);
      }
    });
  }

  downloadFile(vin: string, file: MockedFile, type: FileType): void {
    this.backofficeService.getFile(vin, file.id, type).subscribe({
      next: (mockedFile) => {
        const jsonString = JSON.stringify(JSON.parse(mockedFile.fileContent), null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = file.filename;
        a.click();

        URL.revokeObjectURL(url);
        this.hasLoadError.set(false);
      },
      error: (err: HttpErrorResponse) => {
        let errorMessage: string;

        if (err.status === 404) {
          this.loadMockedVinData(vin);
          errorMessage = this.translateService.instant('backoffice.downloadFile.error.notFound');
          this.sendToast('download-file-not-found', BaseTone.CAUTION, errorMessage);
        } else {
          this.hasLoadError.set(true);
          errorMessage = this.translateService.instant('backoffice.downloadFile.error.generic');
          this.sendToast('download-file-error', BaseTone.CRITICAL, errorMessage);
        }
      },
    });
  }

  previewFile(file: MockedFile): void {
    const inputs = new Map<string, unknown>([['file', file]]);
    this.modalService.openDialog(PreviewFileComponent, inputs);
  }

  openQueueFileModal(vin: string): void {
    const uploadQueueFileComponent = this.modalService.openDialog(UploadQueueFileComponent);

    uploadQueueFileComponent.queueFileSelected.subscribe((filedAdded) => {
      if (filedAdded) {
        this.backofficeService
          .addFileToMockedQueue(vin, filedAdded.selectedFile, filedAdded.selectedQueue, filedAdded.selectedAction)
          .subscribe({
            next: (newQueueState) => {
              const successMessage = this.translateService.instant('common.success');
              this.sendToast('add-file-success', BaseTone.POSITIVE, successMessage);

              const newMockedVin = structuredClone(this.mockedVin());
              newMockedVin.mockedQueue = newQueueState;
              this.mockedVin.set(newMockedVin);
              this.hasLoadError.set(false);
            },
            error: (err: HttpErrorResponse) => {
              const errorMessage = this.translateService.instant(
                err.status === 400 ? 'error.invalidFile' : 'error.generic'
              );
              this.sendToast('mocked-vin-data-fetch-failed', BaseTone.CRITICAL, errorMessage);
            },
          });
      }
    });
  }

  reOrderQueueFiles(clonedMockedQueue: QueueFile[], vin: string, from: number, to: number): void {
    this.backofficeService.reOrderQueueFiles(vin, from, to).subscribe({
      next: () => {
        const successMessage = this.translateService.instant('common.success');
        this.sendToast('re-order-file-sucesss', BaseTone.POSITIVE, successMessage);
      },
      error: () => {
        const newMockedVin = structuredClone(this.mockedVin());
        newMockedVin.mockedQueue = clonedMockedQueue;
        this.hasLoadError.set(true);
        const errorMessage = this.translateService.instant('error.generic');
        this.sendToast('re-order-file-failed', BaseTone.CRITICAL, errorMessage);
      },
    });
  }

  openComfyActionPlanModal(vin: string): void {
    const inputs = new Map<string, unknown>([['vin', vin]]);
    this.modalService.openDialog(ComfyActionPlanModalComponent, inputs);
  }

  setMockedVinData(mockedVinData: MockedVin[]): void {
    this.mockedVinData.set(mockedVinData);
  }

  setActionPlan(actionPlan: ActionPlan[]): void {
    this.actionPlan.set(actionPlan);
  }

  goToMockedInterface(vin: string): void {
    this.routerService.navigate([`/backoffice/mocking/${vin}`]);
  }

  navigateToMocking(vin: string): void {
    this.mockedVin.set({} as MockedVin);
    this.routerService.navigate([`/backoffice/mocking/${vin}`]);
  }

  navigateToBackoffice(): void {
    this.mockedVin.set({} as MockedVin);
    this.routerService.navigate(['/backoffice']);
  }

  private mapMockedVins(mockedVins: MockedVin[]): MockedVin[] {
    const locale = this.localeService.angularLocale();
    return mockedVins.map((mockedVin) => ({
      ...mockedVin,
      updatedBy: mockedVin.updatedBy || mockedVin.createdBy,
      updatedAt:
        this.datePipe.transform(mockedVin.updatedAt, 'shortDate', undefined, locale) ||
        this.datePipe.transform(mockedVin.createdAt, 'shortDate', undefined, locale) ||
        '-',
    }));
  }

  sendToast(id: string, tone: BaseTone, toastText: string): void {
    this.toastService.pushToast({
      id,
      tone,
      toastText,
    } as DsToastModel);
  }

  loadActionPlan(vin: string): void {
    this.backofficeService
      .getActionPlan(vin)
      .pipe(map((response) => response as ActionPlan[]))
      .subscribe({
        next: (actionPlanList) => {
          this.setActionPlan(actionPlanList);
          this.hasLoadError.set(false);
        },
        error: (err) => {
          this.hasLoadError.set(true);
          console.error(err);
        },
      });
  }

  updateActionPlan(vin: string, actionPlan: ActionPlan[]): void {
    this.backofficeService.updateActionPlan(vin, actionPlan).subscribe({
      next: () => {
        const successMessage = this.translateService.instant('common.success');
        this.sendToast('action-plan-update-success', BaseTone.POSITIVE, successMessage);
        this.hasLoadError.set(false);
      },
      error: () => {
        this.hasLoadError.set(true);
        const errorMessage = this.translateService.instant('error.generic');
        this.sendToast('action-plan-update-failed', BaseTone.CRITICAL, errorMessage);
      },
    });
  }
}
